import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import loc from '../../utils/loc';
import { calculateRangePercentage, scrollToRef } from '../../utils/helpers';
import State from '../../context';

import Quiz from '../Quiz';
import Result from '../result';
import LandingPage from '../LandingPage';
import Content from '../Content';
import Layout from '../Layout';
import PreResult from '../preResult';

const Widget = ({ quiz, settings, translations }) => {
  // Store app loading state
  const [loading, setLoading] = useState(true);
  // Store current question index (used for pagination)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  // Store user's answers as question_uuid:option_uuid pairs
  const [answers, setAnswers] = useState({});
  // Store quiz completed status (all questions answered)
  const [complete, setComplete] = useState(false);
  // Store user's results
  const [results, setResults] = useState(false);
  // Show "show results"
  const [showPreResult, setShowPreResult] = useState(false);
  // Quiz answered and results shown. The end.
  const [done, setDone] = useState(false);
  // In paginated mode: user proceeded from landing page to actual questions. No effect if not paginated.
  const [started, setStarted] = useState(
    settings.showLandingPage ? false : true
  );
  // Store reference to widget dom element for scrolling purposes
  const widgetRef = useRef(null);

  // Init once on load
  useEffect(() => {
    // Initialize localization with translations
    loc.init(translations);

    // Format questions
    quiz.questions.forEach((q, i) => {
      q.index = i;
    });

    // Count total points if in points mode
    if (quiz.mode === 'points') {
      quiz.totalPoints = quiz.questions.length;
    }

    // Single
    if (quiz.mode === 'single') {
      quiz.totalPoints = 1;
    }

    setLoading(false);
  }, [quiz, translations]);

  // Check after any answer change if quiz is complete
  useEffect(() => {
    /**
     * Checks if all questions have been answered
     *
     * @return {boolean} complete
     */
    const checkComplete = () => {
      const complete = quiz.questions.every((q) => {
        if (q.type !== 'multiselect') {
          return typeof answers[q.uuid] !== 'undefined';
        } else {
          // Multiselects need to have either all correct answers or at least one wrong answer
          const correctAnswer = quiz.results[0]?.scoring[q.uuid]?.split(',') || [];
          return typeof answers[q.uuid] !== 'undefined' && (answers[q.uuid].length === correctAnswer.length || answers[q.uuid].some(a => !correctAnswer.includes(a)));
        }
      });

      return complete;
    };

    const complete = checkComplete();
    setComplete(complete);
  }, [answers, quiz.questions]);

  // Scroll to top after question changes
  useEffect(() => {
    const el = document.querySelector('.js-quiz');
    if (el && settings.paginated) el.scrollIntoView({ behavior: 'smooth' });
  }, [done, currentQuestionIndex]);

  let filteredQuestions = [...quiz.questions];

  // Show one question at a time if in paginated mode
  if (settings.paginated) {
    filteredQuestions = quiz.questions.filter(
      (q) => q.index === currentQuestionIndex
    );
  }

  /**
   * Resets the quiz to inital state
   */
  const reset = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setResults(false);
    setStarted(false);
    scrollToRef(widgetRef);
  };

  /**
   * Calculates user answer similarity in percents for every result answer
   * "Vaalikone" type logic.
   *
   *  @return {object} calculations
   */
  const calculatePercentageResults = () => {
    const calculations = {};
    const questions = {};

    quiz.questions.forEach((q) => {
      questions[q.uuid] = {
        type: q.type,
        settings: q.settings,
      };
    });

    // Calculate user answer similarity in percent for every result answer
    quiz.results.forEach((result) => {
      const resultCalc = {};

      Object.keys(answers).forEach((questionId) => {
        const questionData = questions[questionId];
        let scoreAnswer = result.scoring[questionId];
        const userAnswer = answers[questionId];

        if (questionData.type === 'slider') {
          const percentage = calculateRangePercentage(
            scoreAnswer,
            userAnswer,
            questionData.settings.min,
            questionData.settings.max
          );
          resultCalc[questionId] = percentage;
        } else if (questionData.type === 'select') {
          const percentage = scoreAnswer === userAnswer ? 100 : 0;
          resultCalc[questionId] = percentage;
        } else if (questionData.type === 'multiselect') {
          if (typeof scoreAnswer === 'undefined' || scoreAnswer.length === 0) {
            // Question not scored at all. Similarity is 0% automatically, because end user has to answer all questions.
            resultCalc[questionId] = 0;
          } else {
            const difference = scoreAnswer.filter(
              (x) => !userAnswer.includes(x)
            );
            const percentage =
              100 - (difference.length / scoreAnswer.length) * 100;
            resultCalc[questionId] = percentage;
          }
        }
      });

      // Calculate average from each question
      const values = Object.values(resultCalc);
      const sum = values.reduce(
        (total, currentValue) => total + currentValue,
        0
      );
      const average = sum / values.length;

      calculations[result.uuid] = Math.round(average);
    });

    return calculations;
  };

  /**
   * Calculates points for quiz, where correct answer = 1 point.
   * Single select & slider: Correct answer = 1 point.
   * Multiselects can have fraction poins: user selects 2/3 correct options = 0.666 points.
   *
   * @return {object} calculations
   */
  const calculatePointsResults = () => {
    const calculations = {};
    const questions = {};

    quiz.questions.forEach((q) => {
      questions[q.uuid] = {
        type: q.type,
        settings: q.settings,
      };
    });

    quiz.results.forEach((result) => {
      const resultCalc = {};

      Object.keys(answers).forEach((questionId) => {
        const questionData = questions[questionId];
        const scoreAnswer = result.scoring[questionId];
        const userAnswer = answers[questionId];

        if (questionData.type === 'slider' || questionData.type === 'select') {
          const points = scoreAnswer === userAnswer ? 1 : 0;
          resultCalc[questionId] = points;
        } else if (questionData.type === 'multiselect') {
          const correctAnswers = scoreAnswer.split(',').map((x) => x.trim());
          const correctUserAnswers = userAnswer.filter((x) =>
            correctAnswers.includes(x)
          );
          const points = correctUserAnswers.length / correctAnswers.length;

          resultCalc[questionId] = points;
        }
      });

      // Calculate total points from each question
      const values = Object.values(resultCalc);
      const sum = values.reduce(
        (total, currentValue) => total + currentValue,
        0
      );

      calculations[result.uuid] = sum;
    });

    return calculations;
  };

  /**
   * Single mode results, doesn't really matter what we return
   */
  const calculateSingleResults = () => {
    const calculations = {};

    quiz.results.forEach((result) => {
      calculations[result.uuid] = 1;
    });

    return calculations;
  }

  /**
   * Calculates final quiz result based on quiz mode.
   */
  const calculateResults = () => {
    let calculations;
    if (quiz.mode === 'points') {
      calculations = calculatePointsResults();
    } else if (quiz.mode === 'single') {
      calculations = calculateSingleResults();
    } else {
      calculations = calculatePercentageResults();
    }

    setResults(calculations);
  };

  useEffect(() => {
    if (complete && results) {
      setDone(true);
    } else {
      if (complete && !results && !showPreResult) {
        if (settings.skipPreResult) {
          calculateResults();
        } else {
          setShowPreResult(true);
        }
      }
      if (done) {
        setDone(false);
      }
    }
  }, [complete, results]);

  /**
   * Renders quiz questions
   *
   * @return {JSX}
   */
  const renderQuiz = () => {
    return (
      <>
        <Quiz
          quiz={quiz}
          questions={filteredQuestions}
          setCurrentQuestionIndex={setCurrentQuestionIndex}
          answers={answers}
          setAnswers={setAnswers}
          done={done}
          finalLink={quiz.finalLink}
          skipQuizText={quiz.skipQuizText}
        />
        {complete && !results && !settings.paginated ? (
          <PreResult onClick={calculateResults} />
        ) : null}
      </>
    );
  };

  /**
   * Renders results
   *
   * @return {JSX}
   */
  const renderResult = () => {
    return (
      <Result
        userResults={results}
        quizResults={quiz.results}
        quizName={quiz.name}
        reset={reset}
        cta={quiz.cta}
        totalPoints={quiz.totalPoints}
        finalLink={quiz.finalLink}
        finalLinkText={quiz.finalLinkText}
      />
    );
  };

  const renderLanding = () => {
    return (
      <LandingPage
        title={quiz.title}
        preTitle={quiz.pretitle}
        setStarted={settings.paginated ? setStarted : null}
        description={quiz.description}
        description2={quiz.description2}
        image={quiz.image}
        image2={quiz.image2}
        finalLink={quiz.finalLink}
        skipQuizText={quiz.skipQuizText}
      />
    );
  };

  /**
   * Renders content based on settings
   *
   * @return {JSX}
   */
  const renderContent = () => {
    // Show landing page, each quiz question and results separately
    if (settings.paginated) {
      if (results) {
        return (
          <Layout image={quiz.image} name={quiz.name} footerImage={quiz.footerImage}>
            {renderResult()}
          </Layout>
        );
      } else if (started) {
        return <Content>{renderQuiz()}</Content>;
      } else if (settings.showLandingPage) {
        return (
          <Layout image={quiz.image} name={quiz.name} footerImage={quiz.footerImage}>
            {renderLanding()}
          </Layout>
        );
      }
    }

    // Show landing page, quiz and results at the same time
    return (
      <Layout image={quiz.image} name={quiz.name} footerImage={quiz.footerImage}>
        {settings.showLandingPage ? (
          <>
            {renderLanding()}
            <Spacer />
          </>
        ) : null}
        {renderQuiz()}
        {results && quiz.mode !== 'single' ? renderResult() : null}
      </Layout>
    );
  };

  return (
    <Wrapper
      ref={widgetRef}
      backgroundImage={quiz.backgroundImage}
      backgroundColor={quiz.backgroundColor}
      backgroundSize={quiz.backgroundSize}
      backgroundRepeat={quiz.backgroundRepeat}
      darkMode={quiz.darkMode}
      className={quiz.darkMode ? 'bg--dark' : ''}
    >
      <State.Provider
        value={{ mode: quiz.mode, settings, calculateResults, showPreResult, texts: {finalLinkText: quiz.finalLinkText, finalLink: quiz.finalLink} }}
      >
        {loading ? null : renderContent()}
      </State.Provider>
    </Wrapper>
  );
};

Widget.propTypes = {
  quiz: PropTypes.object,
  settings: PropTypes.object,
  translations: PropTypes.object,
};

const Wrapper = styled.div`
  // In NT case this is the light mode
  --quiz-app-background-color: transparent; // Show parent page background color by default
  --quiz-content-background-color: #fff;
  --quiz-option-background-color: #ccc;
  --quiz-text-color: currentColor; // Take parent page text color. This needs to work on both app bg and content bg colors.
  --quiz-text-on-primary-color: #fff;
  --quiz-primary-color: #51b848;

  ${(props) =>
    props.darkMode
      ? `
    --quiz-app-background-color: #000;
    --quiz-content-background-color: #333;
    --quiz-option-background-color: #1d1d1d;
    --quiz-text-color: #fff;
    --quiz-text-on-primary-color: #fff;
    --quiz-primary-color: #51b848;
  `
      : ''}

  background-color: ${(props) =>
    props.backgroundColor ? props.backgroundColor : 'transparent'};
  background-image: ${(props) =>
    props.backgroundImage ? 'url(' + props.backgroundImage + ')' : 'none'};
  background-size: ${(props) =>
    props.backgroundSize ? props.backgroundSize : '150%'};
  background-repeat: ${(props) =>
    props.backgroundRepeat ? props.backgroundRepeat : 'repeat'};
  border-top: 5px solid var(--quiz-primary-color);
  color: var(--quiz-text-color);
  padding: 3rem 0;
  position: relative;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  // Override weird NT styles
  [aria-hidden="true"] {
    display: unset;
  }
`;

const Spacer = styled.div`
  height: 2rem;
`;

export default Widget;
